import { useCallback, useEffect, useRef } from "preact/hooks"
import { RefObject } from "preact"
import { useDeviceType } from "./useDeviceType"

function useHorizontalScrollOnVerticalScroll(
  containerRef: RefObject<HTMLDivElement>,
  firstRowRef: RefObject<HTMLDivElement>,
  secondRowRef?: RefObject<HTMLDivElement> | null,
) {
  const initialScrollY = useRef<number | null>(null)
  const animationFrameId = useRef<number | null>(null)

  const isMobile = useDeviceType() === "mobile"

  const onScroll = useCallback(() => {
    if (!firstRowRef.current || !containerRef.current) return

    const scrollY = window.scrollY
    const containerTop = containerRef.current.offsetTop
    const containerHeight = containerRef.current.offsetHeight

    const isInView =
      scrollY >= containerTop - window.innerHeight &&
      scrollY <= containerTop + containerHeight

    if (!isInView) return

    if (initialScrollY.current === null) {
      initialScrollY.current = scrollY
    }

    const deltaY = scrollY - initialScrollY.current
    const scrollX = deltaY * 0.2

    const firstMaxScroll =
      firstRowRef.current.scrollWidth - firstRowRef.current.clientWidth
    const secondMaxScroll =
      secondRowRef?.current?.scrollWidth - secondRowRef?.current?.clientWidth ||
      0

    if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current)

    animationFrameId.current = requestAnimationFrame(() => {
      firstRowRef.current!.scrollLeft = Math.min(scrollX, firstMaxScroll)
      if (secondRowRef?.current) {
        secondRowRef.current.scrollLeft = Math.max(secondMaxScroll - scrollX, 0)
      }
    })
  }, [containerRef, firstRowRef, secondRowRef])

  useEffect(() => {
    if (isMobile) return

    window.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      if (animationFrameId.current)
        cancelAnimationFrame(animationFrameId.current)
      window.removeEventListener("scroll", onScroll)
    }
  }, [onScroll, isMobile])
}

export default useHorizontalScrollOnVerticalScroll
